//do not import from index here, we do not want to bundle the whole cf+core into the debug build
import { getDefaultGetArticleDataOptions } from "../cf/Defaults";
import * as basket from "../eaiws/basket";
import { GroupedMap } from "../utils";
import { clone, removeElement } from "../utils/array";
import { forEachParallel } from "../utils/async";
import { isNotNullOrEmpty, isNullOrEmpty } from "../utils/string";
import { MAX_CONCURRENT_TASKS, BASKET_COLUMN_ID_EXT_ITEM_NUMBER, BASKET_COLUMN_ID_USER_IMAGE, BASKET_COLUMN_ID_COMMENTS, BASKET_COLUMN_ID_ADDITIONAL_IMAGES } from "./Constants";
export const ItemUpdateFlags = {
    None: 0x00000000,
    Item: 0x00000001,
    ItemProps: 0x00000002,
    Image: 0x00000004,
    Calculation: 0x00000008,
    Tax: 0x00000010,
    ConfigData: 0x00000020,
    UserImage: 0x00000040,
    ExtItemNumber: 0x00000080,
    UserItemDescription: 0x00000100,
    UserArticleDescription: 0x00000100,
    AdditionalImages: 0x00000200,
    Comments: 0x00000400,
    IncludeChildren: 0x80000000,
    /** UserFields = UserImage | ExtItemNumber | UserItemDescription | AdditionalImages */
    UserFields: 0x00000040 | 0x00000080 | 0x00000100 | 0x00000200,
    All: 0xFFFFFFFF
};
export class BaseItem {
    get id() {
        return (this.mItem.itemId);
    }
    get label() {
        return (this.mItem.label);
    }
    get type() {
        return (this.mItem.itemType);
    }
    get item() {
        return (this.mItem);
    }
    get parent() {
        return (this.mParent);
    }
    get children() {
        return (this.mChildren);
    }
    /** Index of this item inside the children array of its parent. */
    get childIndex() {
        return (this.mChildIndex);
    }
    get isSelected() {
        return (this.mIsSelected);
    }
    get isDisposed() {
        return (this.mIsDisposed);
    }
    /**
     * Can be used to store runtime user/custom data. This data is not persistent.
     * Use BasketService.setItemAppData() for persistent data.
     */
    get userData() {
        return (this.mUserData);
    }
    get isGroup() {
        return (false);
    }
    /**
     * Returns true if this item represents an configurable OFML article.
     */
    get isOfmlArticle() {
        return (false);
    }
    constructor(pManager, pParent, pChildIndex, pEaiwsItem) {
        this.mManager = pManager;
        this.mParent = pParent;
        this.mItem = pEaiwsItem;
        this.mChildren = new Array();
        this.mImageUrl = undefined;
        this.mUserData = new GroupedMap();
        this.mIsSelected = false;
        this.mIsDisposed = false;
        this.mChildIndex = pChildIndex;
        this.mCalculationHdrVersion = this.mManager._getHeaderCalcVersion();
        //init from cache
        this.useItemPropsFromCache();
        this.useCalculationFromCache();
        //register item instance
        this.registerItem();
    }
    canEditLabel() {
        return ("setLabel" in this);
    }
    /**
     * Returns true if this item is excluded from the calculations for the currently active
     * pricing procedure (e.g. its an alternative or optional item). Returns undefined if the state is
     * unknown or its an unsupported item. Undefined will be also returned if the ItemProperties
     * are not available. get or fetchItemProperties() should be called before to ensure that they are available.
     */
    isExcludedFromCalculations() {
        return (this.getInactivePositionState() != null);
    }
    /**
     * Returns if this item is marked as alternative or optional for the currently active pricing procedure.
     * Returns undefined if the state is unknown or its an unsupported item. Undefined will be also
     * returned if the ItemProperties are not available. get or fetchItemProperties() should be
     * called before to ensure that they are available.
     */
    getInactiveState() {
        const tState = this.getInactivePositionState();
        if (tState === undefined)
            return (undefined);
        if (tState === null)
            return ("None");
        return (tState.optional === true ? "Optional" : "Alternative");
    }
    /**
     * Note: The setArticleId which is part of the ItemProperties may not be up-to-date.
     * Use this.item.setArticleId or this.getSetArticle() instead.
     */
    async getItemProperties() {
        await this.fetchItemProperties(false, undefined);
        if (this.mItemProps === undefined)
            throw new Error("Failed to get item properties: " + this.id);
        return (this.mItemProps);
    }
    getCachedItemProperties() {
        return (this.mItemProps);
    }
    /**
     * Fetches the item properties if necessary and adds them to the cache.
     * @param pIncludeChildren If true the item properties of child items will be fetched too.
     * @param pShouldFetchCallback Can be used to ignore certain items. The callback has to return false in this case.
     */
    async fetchItemProperties(pIncludeChildren, pShouldFetchCallback) {
        const tLockContext = await this.mManager._fetchItemPropertiesLock.acquireLock();
        try {
            //collect items without item props
            const tCollected = this.collectItemsToFetch((pItem) => pItem.mItemProps == null, pIncludeChildren, pShouldFetchCallback);
            //fetch unknown item properties
            if (tCollected.length > 0) {
                try {
                    this.mManager._beginCaching();
                    await this.mManager._fetchItemProps(tCollected);
                    //update all collected items
                    for (const tItem of tCollected)
                        tItem.useItemPropsFromCache();
                }
                finally {
                    this.mManager._endCaching();
                }
            }
        }
        finally {
            tLockContext.unlock();
        }
    }
    async getImageUrl() {
        if (this.mImageUrl == null) {
            await this.generateImageUrl();
        }
        if (this.mImageUrl != undefined)
            return (this.mImageUrl);
        return (null);
    }
    getCachedImageUrl() {
        return (this.mImageUrl);
    }
    async getUserImageUrl() {
        await this.fetchUserImageUrls(false, undefined);
        if (this.mUserImageUrl === undefined)
            throw new Error("Failed to get item user image: " + this.id);
        return (this.mUserImageUrl);
    }
    getCachedUserImageUrl() {
        return (this.mUserImageUrl);
    }
    /**
     * Fetches the user defined images if necessary and adds them to the cache.
     * @param pIncludeChildren If true the user image of child items will be fetched too.
     * @param pShouldFetchCallback Can be used to ignore certain items. The callback has to return false in this case.
     */
    fetchUserImageUrls(pIncludeChildren, pShouldFetchCallback) {
        return (this.fetchItemFields(pIncludeChildren, pShouldFetchCallback));
    }
    /**
     * Returns the additional images for this item.
     * Note: `undefined` images should be handled as missing/broken images
     */
    async getAdditionalImageUrls() {
        await this.fetchAdditionalImageUrls(false, undefined);
        if (this.mAdditionalImageUrls === undefined)
            throw new Error("Failed to get additional images for item: " + this.id);
        return (this.mAdditionalImageUrls);
    }
    /**
     * Returns the additional images for this item from cache.
     * Note: `undefined` images should be handled as missing/broken images
     */
    getCachedAdditionalImageUrls() {
        return (this.mAdditionalImageUrls);
    }
    /**
     * Fetches the user defined additional images if necessary and adds them to the cache.
     * @param pIncludeChildren If true the additional images of child items will be fetched too.
     * @param pShouldFetchCallback Can be used to ignore certain items. The callback has to return false in this case.
     */
    fetchAdditionalImageUrls(pIncludeChildren, pShouldFetchCallback) {
        return (this.fetchItemFields(pIncludeChildren, pShouldFetchCallback));
    }
    /**
     * Returns the item calculation.
     * @param pIgnoreHeaderCalculation If true the cached calculation will be returned even if the
     *      header calculation has changed in the meantime. Every condition which depends on a header condition
     *      may have a wrong/outdated value in this case. (default: false)
     */
    async getCalculation(pIgnoreHeaderCalculation = false) {
        await this.fetchCalculations(false, undefined, pIgnoreHeaderCalculation);
        if (this.mCalculation === undefined)
            throw new Error("Failed to get item calculation: " + this.id);
        return (this.mCalculation);
    }
    /**
     * Returns the cached item calculation.
     * @param pIgnoreHeaderCalculation If true the cached calculation will be returned even if the
     *      header calculation has changed in the meantime. Every condition which depends on a header condition
     *      may have a wrong/outdated value in this case. (default: false)
     */
    getCachedCalculation(pIgnoreHeaderCalculation = false) {
        return (this.isCachedCalculationValid(pIgnoreHeaderCalculation) ? this.mCalculation : undefined);
    }
    /**
     * Fetches the calculations if necessary and adds them to the cache.
     * @param pIncludeChildren If true the calculations of child items will be fetched too.
     * @param pShouldFetchCallback Can be used to ignore certain items. The callback has to return false in this case.
     * @param pIgnoreHeaderCalculation If true the cached calculation will be returned even if the
     *      header calculation has changed in the meantime. Every condition which depends on a header condition
     *      may have a wrong/outdated value in this case. (default: false)
     */
    async fetchCalculations(pIncludeChildren, pShouldFetchCallback, pIgnoreHeaderCalculation = false) {
        const tLockContext = await this.mManager._fetchCalculationsLock.acquireLock();
        try {
            //collect items without calculation
            const tCollected = this.collectItemsToFetch((pItem) => !pItem.isCachedCalculationValid(pIgnoreHeaderCalculation), pIncludeChildren, pShouldFetchCallback);
            //fetch unknown item calculations
            if (tCollected.length > 0) {
                try {
                    this.mManager._beginCaching();
                    const tHeaderCalcVersion = this.mManager._getHeaderCalcVersion();
                    await this.mManager._fetchItemCalculations(tCollected);
                    //update all collected items
                    for (const tItem of tCollected) {
                        tItem.mCalculationHdrVersion = tHeaderCalcVersion;
                        tItem.useCalculationFromCache();
                    }
                }
                finally {
                    this.mManager._endCaching();
                }
            }
        }
        finally {
            tLockContext.unlock();
        }
    }
    async getExtItemNumber() {
        await this.fetchExtItemNumbers(false, undefined);
        if (this.mExtItemNumber === undefined)
            throw new Error("Failed to get external item number: " + this.id);
        return (this.mExtItemNumber);
    }
    getCachedExtItemNumber() {
        return (this.mExtItemNumber);
    }
    /**
     * Fetches the external item numbers if necessary and adds them to the cache.
     * @param pIncludeChildren If true the item number of child items will be fetched too.
     * @param pShouldFetchCallback Can be used to ignore certain items. The callback has to return false in this case.
     */
    fetchExtItemNumbers(pIncludeChildren, pShouldFetchCallback) {
        return (this.fetchItemFields(pIncludeChildren, pShouldFetchCallback));
    }
    async getComments() {
        await this.fetchComments(false, undefined);
        if (this.mComments === undefined)
            throw new Error("Failed to get comments: " + this.id);
        return (this.mComments);
    }
    getCachedComments() {
        return (this.mComments);
    }
    /**
     * Fetches the comments if necessary and adds them to the cache.
     * @param pIncludeChildren If true the comments of child items will be fetched too.
     * @param pShouldFetchCallback Can be used to ignore certain items. The callback has to return false in this case.
     */
    fetchComments(pIncludeChildren, pShouldFetchCallback) {
        return (this.fetchItemFields(pIncludeChildren, pShouldFetchCallback));
    }
    invalidateGroupCalculations() {
        if (this.isGroup)
            this.invalidateCalculation(true);
        const tSetArticle = this.internalGetSetArticle();
        const tParentGroup = this.getParentGroup();
        //update set article if it is not the direct parent group
        if (tSetArticle != null && tSetArticle != tParentGroup) {
            tSetArticle.invalidateGroupCalculations();
        }
        if (tParentGroup != null)
            tParentGroup.invalidateGroupCalculations();
        else
            this.mManager.invalidateHeaderCalculation();
    }
    getParentGroup() {
        if (this.mParent == null)
            return (null);
        if (this.mParent.isGroup)
            return (this.mParent);
        else
            return (this.mParent.getParentGroup());
    }
    async updateItem(pFlags) {
        try {
            this.mManager._beginItemUpdate();
            this.mManager._beginCaching();
            //fetch all items before, to speed up update
            if ((pFlags & ItemUpdateFlags.Item) != 0 && (pFlags & ItemUpdateFlags.IncludeChildren) != 0) {
                await this.mManager._fetchEaiwsItems([this.id], true, false);
            }
            await this.performUpdates(pFlags);
            if ((pFlags & ItemUpdateFlags.Calculation) != 0)
                this.invalidateGroupCalculations();
        }
        finally {
            this.mManager._endCaching();
            this.mManager._endItemUpdate();
        }
    }
    isChildOf(pParent, pDirectOnly) {
        if (pParent == null || pParent === this.asType)
            return (false);
        if (pDirectOnly) {
            return (this.mParent === pParent);
        }
        else {
            let tParent = this.mParent;
            while (tParent != null) {
                if (tParent === pParent)
                    return (true);
                tParent = tParent.parent;
            }
            return (false);
        }
    }
    getPrevChild(pChild) {
        let tDirectChild = pChild;
        //pChild maybe an indirect child, go up the hierarchy to find direct child
        while (tDirectChild != null && tDirectChild.parent !== this.asType)
            tDirectChild = tDirectChild.parent;
        if (tDirectChild == null)
            return (null);
        const tIndex = tDirectChild.mChildIndex;
        if (tIndex > 0)
            return (this.mChildren[tIndex - 1]);
        return (null);
    }
    getNextChild(pChild) {
        let tDirectChild = pChild;
        //pChild maybe an indirect child, go up the hierarchy to find direct child
        while (tDirectChild != null && tDirectChild.parent !== this.asType)
            tDirectChild = tDirectChild.parent;
        if (tDirectChild == null)
            return (null);
        const tIndex = tDirectChild.mChildIndex;
        if (tIndex >= 0 && (tIndex + 1) < this.mChildren.length)
            return (this.mChildren[tIndex + 1]);
        return (null);
    }
    getItemNumber() {
        if (this.mItemNumber != null)
            return (this.mItemNumber);
        this.mItemNumber = this._getItemNumberAsArray().join(".");
        return (this.mItemNumber);
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    async _rebuildChildList(pFlags) {
        const tIsCaching = this.mManager._isCaching();
        //build map of current children
        const tDeletedChildsMap = new Map();
        for (const tChild of this.mChildren)
            tDeletedChildsMap.set(tChild.id, tChild);
        //create list of new childs
        //operate on new array to avoid conflicts if the same item is updated multiple times in parallel
        const tNewChildren = new Array();
        if (this.mItem.subItemIds != null) {
            const tUpdateChilds = (pFlags & ItemUpdateFlags.IncludeChildren) != 0;
            for (const tSubItemId of this.mItem.subItemIds) {
                const tChildIndex = tNewChildren.length;
                //check if already created
                let tItem = this.mManager.getItemById(tSubItemId);
                if (tItem != null) {
                    tItem.setParent(this.asType, tChildIndex); //parent or childIndex may have changed
                    if (tUpdateChilds) {
                        await tItem.performUpdates(pFlags);
                    }
                }
                else {
                    //create new item
                    tItem = await this.mManager._createItem(tSubItemId, this.asType, tChildIndex, true);
                }
                if (tItem != null) {
                    tNewChildren.push(tItem);
                    //remove child from deleted list
                    tDeletedChildsMap.delete(tItem.id);
                    //remove from deleted items cache
                    if (tIsCaching)
                        this.mManager._deletedItemsCache.delete(tItem.id);
                }
            }
        }
        //assig new children list
        this.mChildren = tNewChildren;
        if (tIsCaching) {
            //mark for delayed disposing
            tDeletedChildsMap.forEach((pChild) => this.mManager._deletedItemsCache.set(pChild.id, pChild));
        }
        else {
            //dispose deleted children
            tDeletedChildsMap.forEach((pChild) => pChild._dispose());
        }
    }
    /** @internal */
    _dispose() {
        if (this.isDisposed)
            return;
        this.mManager.eventItemDisposed.trigger(this.asType);
        this.mManager._deregisterItem(this.asType);
        for (const tChild of this.mChildren)
            tChild._dispose();
        this.mChildren = []; //empty the array to help garbage collection of items
        this.mIsDisposed = true;
    }
    /** @internal */
    _setIsSelected(pSelected) {
        this.mIsSelected = pSelected;
    }
    /** @internal */
    _getArticleData(pOptions) {
        const tRequestedOptions = pOptions !== null && pOptions !== void 0 ? pOptions : getDefaultGetArticleDataOptions();
        tRequestedOptions.separateCurrencies = true; //force to be true
        return (this.mManager.session.basket.getArticleData(this.id, tRequestedOptions));
    }
    /** @internal */
    _getItemNumberAsArray() {
        if (this.mItemNumberArray != null)
            return (this.mItemNumberArray);
        if (this.mParent == null) {
            this.mItemNumberArray = [];
        }
        else {
            this.mItemNumberArray = clone(this.mParent._getItemNumberAsArray());
            this.mItemNumberArray.push(this.mChildIndex + 1);
        }
        return (this.mItemNumberArray);
    }
    invalidateCalculation(pTriggerUpdateEvent) {
        //reset calculation info (use cached info)
        this.useCalculationFromCache();
        if (pTriggerUpdateEvent) {
            this.mManager.eventItemUpdated.trigger({ item: this.asType, flags: ItemUpdateFlags.Calculation });
        }
    }
    isCachedCalculationValid(pIgnoreHeaderCalculation) {
        if (this.mCalculation == null)
            return (false);
        return (pIgnoreHeaderCalculation ? true : this.mCalculationHdrVersion === this.mManager._getHeaderCalcVersion());
    }
    getGeneratedImage(pCamSetup) {
        let tOptions = this.mManager._getGeneratedImageOptions();
        const tViewId = this.mManager._getViewId();
        if (tViewId != null || pCamSetup != null) {
            tOptions = clone(tOptions);
            if (pCamSetup != null) {
                tOptions.push("alpha=" + pCamSetup.alpha.toString(), "beta=" + pCamSetup.beta.toString(), "zoom=" + pCamSetup.fov.toString(), "projectionMode=" + pCamSetup.mode);
            }
            if (tViewId != null) {
                tOptions.push("viewId=" + tViewId);
            }
        }
        const tPromise = this.mManager.session.basket.getGeneratedImage(this.id, tOptions).then((pUrl) => {
            if (!isNullOrEmpty(pUrl))
                return (pUrl);
            else
                return (null);
        });
        return (tPromise);
    }
    getCatalogImage() {
        const tOptions = new basket.GetArticleDataOptions();
        tOptions.noProperties = true;
        tOptions.fetchCatalogIcon = true;
        tOptions.fetchCatalogImage = true;
        tOptions.viewId = this.mManager._getViewId();
        const tPromise = this._getArticleData(tOptions).then((pData) => {
            let tCatalogImage = undefined;
            tCatalogImage = pData.catalogImage;
            if (isNullOrEmpty(tCatalogImage))
                tCatalogImage = pData.catalogIcon;
            if (!isNullOrEmpty(tCatalogImage))
                return (tCatalogImage);
            else
                return (null);
        });
        return (tPromise);
    }
    async getStoredImage() {
        const tOptions = new basket.GetImagesOptions();
        tOptions.useCache = true;
        tOptions.attributes = true;
        tOptions.viewId = this.mManager._getViewId();
        let tLegacyPBKImage = null;
        const tImages = await this.mManager.session.basket.getImages(this.id, tOptions);
        if (tImages != null) {
            for (const tInfo of tImages) {
                //the shared configuration dependent image has an empty tag or the `default` tag
                //an empty tag is used for the legacy PBK images
                //images with other tags may not match the current article configuration
                if (isNotNullOrEmpty(tInfo.url)) {
                    if (tInfo.tag === "default") {
                        return (tInfo);
                    }
                    else if (isNullOrEmpty(tInfo.tag)) {
                        tLegacyPBKImage = tInfo;
                    }
                }
            }
        }
        return (tLegacyPBKImage);
    }
    getCameraSetupFromImage(pImage) {
        if (pImage.attributes == null || pImage.attributes.length === 0)
            return (null);
        let tAlpha;
        let tBeta;
        let tZoom;
        let tProjMode;
        for (const tAttr of pImage.attributes) {
            if (tAttr.startsWith("alpha"))
                tAlpha = this.getImageAttributeValue(tAttr);
            else if (tAttr.startsWith("beta"))
                tBeta = this.getImageAttributeValue(tAttr);
            else if (tAttr.startsWith("zoom"))
                tZoom = this.getImageAttributeValue(tAttr);
            else if (tAttr.startsWith("projMode"))
                tProjMode = this.getImageAttributeValue(tAttr);
        }
        if (tAlpha == null || tBeta == null)
            return (null);
        const tSetup = {
            alpha: tAlpha,
            beta: tBeta,
            mode: tProjMode === 1 ? "Orthographic" : "Perspective",
            fov: 30
        };
        if (tZoom != null && tSetup.mode === "Perspective") {
            tSetup.fov = tZoom;
        }
        return (tSetup);
    }
    generateImageUrl() {
        this.mImageUrl = null;
        return Promise.resolve();
    }
    async setItemProperties(pProps, pUpdateItem, pUpdateCalculation, pAdditionalUpdateFlags = 0) {
        await this.mManager.session.basket.setItemProperties(this.id, pProps);
        let tFlags = ItemUpdateFlags.ItemProps | pAdditionalUpdateFlags;
        if (pUpdateItem)
            tFlags = tFlags | ItemUpdateFlags.Item;
        if (pUpdateCalculation) {
            tFlags = tFlags | ItemUpdateFlags.Calculation;
            this.invalidateGroupCalculations();
        }
        return this.updateItem(tFlags);
    }
    internalGetSetArticle() {
        if (isNullOrEmpty(this.mItem.setArticleId))
            return (null);
        else {
            return (this.mManager.getItemById(this.mItem.setArticleId));
        }
    }
    async performUpdates(pFlags) {
        //update item
        if ((pFlags & ItemUpdateFlags.Item) != 0)
            this.mItem = await this.mManager._getEaiwsItem(this.id);
        //reset item properties (use cached props)
        if ((pFlags & ItemUpdateFlags.ItemProps) != 0)
            this.useItemPropsFromCache();
        //reset calculation
        if ((pFlags & ItemUpdateFlags.Calculation) != 0)
            this.invalidateCalculation(false);
        //reset image
        if ((pFlags & ItemUpdateFlags.Image) != 0)
            this.mImageUrl = undefined;
        //reset user image
        if ((pFlags & ItemUpdateFlags.UserImage) != 0)
            this.mUserImageUrl = undefined;
        //reset external item number
        if ((pFlags & ItemUpdateFlags.ExtItemNumber) != 0)
            this.mExtItemNumber = undefined;
        //reset comments
        if ((pFlags & ItemUpdateFlags.Comments) != 0)
            this.mComments = undefined;
        //reset additional images
        if ((pFlags & ItemUpdateFlags.AdditionalImages) != 0)
            this.mAdditionalImageUrls = undefined;
        if ((pFlags & ItemUpdateFlags.Item) != 0) {
            await this._rebuildChildList(pFlags);
        }
        else if ((pFlags & ItemUpdateFlags.IncludeChildren) != 0) {
            await forEachParallel(this.mChildren, (pIndex, pChild) => {
                return (pChild.performUpdates(pFlags));
            }, MAX_CONCURRENT_TASKS);
        }
        this.mManager.eventItemUpdated.trigger({ item: this.asType, flags: pFlags });
    }
    handleChangedItemProps() {
    }
    //// private ///////////////////////////////////////////////////////////////////////////////
    useItemPropsFromCache() {
        this.mItemProps = this.mManager._getCachedItemProps(this.id);
        this.handleChangedItemProps();
    }
    useCalculationFromCache() {
        this.mCalculation = this.mManager._getCachedItemCalculation(this.id);
    }
    getImageAttributeValue(pAttribute) {
        const tIndex = pAttribute.indexOf("=");
        if (tIndex < 0)
            return (undefined);
        const tValue = parseFloat(pAttribute.substr(tIndex + 1));
        if (isNaN(tValue))
            return (undefined);
        return (tValue);
    }
    setParent(pParent, pChildIndex) {
        if (pParent == null)
            return;
        const tOldParent = this.mParent;
        this.mParent = pParent;
        this.mChildIndex = pChildIndex;
        this.invalidateItemNumber();
        //remove as child from old parent
        if (tOldParent != null && tOldParent !== this.mParent && tOldParent.mChildren)
            removeElement(tOldParent.mChildren, this.asType);
    }
    invalidateItemNumber() {
        this.mItemNumber = undefined;
        this.mItemNumberArray = undefined;
        for (const tChild of this.mChildren)
            tChild.invalidateItemNumber();
    }
    async fetchItemFields(pIncludeChildren, pShouldFetchCallback) {
        const tLockContext = await this.mManager._fetchItemFieldsLock.acquireLock();
        let tFetchUserImage = false;
        let tFetchExtItemNumber = false;
        let tFetchComments = false;
        let tFetchAdditionalImages = false;
        const tCollectPredicate = (pItem) => {
            let tNeedToFetchItem = false;
            if (pItem.mUserImageUrl === undefined) {
                tFetchUserImage = true;
                tNeedToFetchItem = true;
            }
            if (pItem.mExtItemNumber === undefined) {
                tFetchExtItemNumber = true;
                tNeedToFetchItem = true;
            }
            if (pItem.mComments === undefined) {
                tFetchComments = true;
                tNeedToFetchItem = true;
            }
            if (pItem.mAdditionalImageUrls === undefined) {
                tFetchAdditionalImages = true;
                tNeedToFetchItem = true;
            }
            return (tNeedToFetchItem);
        };
        try {
            //collect items with missing fields
            const tCollected = this.collectItemsToFetch(tCollectPredicate, pIncludeChildren, pShouldFetchCallback);
            //setup column ids
            const tColumns = new Array();
            if (tFetchUserImage) {
                tColumns.push(BASKET_COLUMN_ID_USER_IMAGE);
            }
            if (tFetchExtItemNumber) {
                tColumns.push(BASKET_COLUMN_ID_EXT_ITEM_NUMBER);
            }
            if (tFetchComments) {
                tColumns.push(BASKET_COLUMN_ID_COMMENTS);
            }
            if (tFetchAdditionalImages) {
                tColumns.push(...BASKET_COLUMN_ID_ADDITIONAL_IMAGES);
            }
            //fetch data
            if (tCollected.length > 0 && tColumns.length > 0) {
                const tFieldMap = await this.mManager._getItemFields(tCollected, tColumns);
                //update all collected items
                for (const tItem of tCollected) {
                    const tFields = tFieldMap.get(tItem.id);
                    if (tFetchUserImage) {
                        const tUserImage = tFields === null || tFields === void 0 ? void 0 : tFields.get(BASKET_COLUMN_ID_USER_IMAGE);
                        tItem.mUserImageUrl = this.isValidImageUrl(tUserImage) ? tUserImage : null;
                    }
                    if (tFetchExtItemNumber) {
                        const tExtItemNumber = tFields === null || tFields === void 0 ? void 0 : tFields.get(BASKET_COLUMN_ID_EXT_ITEM_NUMBER);
                        tItem.mExtItemNumber = isNotNullOrEmpty(tExtItemNumber) ? tExtItemNumber : null;
                    }
                    if (tFetchComments) {
                        const tComments = tFields === null || tFields === void 0 ? void 0 : tFields.get(BASKET_COLUMN_ID_COMMENTS);
                        tItem.mComments = isNotNullOrEmpty(tComments) ? tComments : null;
                    }
                    if (tFetchAdditionalImages) {
                        const tImages = new Array();
                        if (tFields != null) {
                            for (let i = 0; i < BASKET_COLUMN_ID_ADDITIONAL_IMAGES.length; ++i) {
                                const tImage = tFields.get(BASKET_COLUMN_ID_ADDITIONAL_IMAGES[i]);
                                if (this.isValidImageUrl(tImage)) { //only assign if valid to get correct array length
                                    tImages[i] = tImage;
                                }
                            }
                        }
                        tItem.mAdditionalImageUrls = tImages;
                    }
                }
            }
        }
        finally {
            tLockContext.unlock();
        }
    }
    collectItemsToFetch(pPredicate, pIncludeChildren, pShouldFetchCallback) {
        const tResult = new Array();
        const collectItems = (pItem) => {
            if ((pShouldFetchCallback == null || pShouldFetchCallback(pItem)) && pPredicate(pItem)) {
                tResult.push(pItem);
            }
            if (pIncludeChildren) {
                for (const tChild of pItem.mChildren) {
                    collectItems(tChild);
                }
            }
        };
        collectItems(this.asType);
        return (tResult);
    }
    isValidImageUrl(pUrl) {
        return (isNotNullOrEmpty(pUrl) && !pUrl.startsWith("imp:"));
    }
    getInactivePositionState() {
        const tProps = this.getCachedItemProperties();
        if (tProps == null || tProps.article == null || tProps.article.inactivePositionState == null)
            return (undefined);
        const tStates = tProps.article.inactivePositionState;
        if (tStates.length === 1 && tStates[0].ppName === "")
            return (tStates[0]); //inactive for all pricing profiles
        const tPProc = this.mManager._getPricingProcedure();
        if (tPProc == null)
            return (undefined);
        for (const tState of tStates) {
            if (tState.ppName === tPProc.name) {
                return (tState);
            }
        }
        return (null);
    }
}
